import { useEffect, useState } from 'react';
import { Mail, Trash2, Plus, Power } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { API_BASE } from '../../utils/api-base';

interface AutomatedReportsProps {
  clientId: string;
  token: string;
}

interface LastReport {
  generatedAt: string;
  emailStatus: 'none' | 'sent' | 'partial' | 'failed';
  bridgeStatus: 'pending' | 'delivered' | 'failed' | 'no_bridge';
  bridgeDeliveredAt: string | null;
  bridgeError: string | null;
}

interface Schedule {
  id: string;
  reportType: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  sendHour: number;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  recipients: string;
  enabled: boolean;
  lastSentAt: string | null;
  lastReport: LastReport | null;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const emptyForm = {
  frequency: 'daily' as 'daily' | 'weekly' | 'monthly',
  sendHour: 8,
  dayOfWeek: 1,
  dayOfMonth: 1,
  recipients: '',
};

export function AutomatedReports({ clientId, token }: AutomatedReportsProps) {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/clients/${clientId}/report-schedules`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await res.json();
      if (res.ok && payload?.success) setSchedules(payload.schedules || []);
    } catch (_) { /* keep last known state */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (clientId && token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, token]);

  const createSchedule = async () => {
    if (!form.recipients.trim()) {
      setError('At least one recipient email is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/clients/${clientId}/report-schedules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          reportType: 'energy',
          frequency: form.frequency,
          sendHour: form.sendHour,
          dayOfWeek: form.frequency === 'weekly' ? form.dayOfWeek : null,
          dayOfMonth: form.frequency === 'monthly' ? form.dayOfMonth : null,
          recipients: form.recipients.trim(),
        }),
      });
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.error || 'Failed to create schedule');
      setForm(emptyForm);
      load();
    } catch (err: any) {
      setError(err?.message || 'Failed to create schedule');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (schedule: Schedule) => {
    try {
      await fetch(`${API_BASE}/api/clients/${clientId}/report-schedules/${schedule.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ enabled: !schedule.enabled }),
      });
      load();
    } catch (_) { /* non-fatal */ }
  };

  const removeSchedule = async (id: string) => {
    try {
      await fetch(`${API_BASE}/api/clients/${clientId}/report-schedules/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setSchedules((prev) => prev.filter((s) => s.id !== id));
    } catch (_) { /* non-fatal */ }
  };

  const STATUS_TONE: Record<string, string> = {
    sent: 'bg-green-100 text-green-700 border-green-200',
    delivered: 'bg-green-100 text-green-700 border-green-200',
    partial: 'bg-amber-100 text-amber-700 border-amber-200',
    pending: 'bg-amber-100 text-amber-700 border-amber-200',
    failed: 'bg-red-100 text-red-700 border-red-200',
    none: 'bg-muted text-muted-foreground',
    no_bridge: 'bg-muted text-muted-foreground',
  };

  const STATUS_LABEL: Record<string, string> = {
    sent: 'Email sent', delivered: 'Bridge delivered', partial: 'Partially sent',
    pending: 'Bridge pending', failed: 'Failed', none: 'No recipients', no_bridge: 'No bridge installed',
  };

  const describeSchedule = (s: Schedule) => {
    const time = `${String(s.sendHour).padStart(2, '0')}:00`;
    if (s.frequency === 'daily') return `Every day at ${time}`;
    if (s.frequency === 'weekly') return `Every ${WEEKDAYS[s.dayOfWeek ?? 1]} at ${time}`;
    return `Day ${s.dayOfMonth ?? 1} of every month at ${time}`;
  };

  if (loading) {
    return <div className="p-6 text-muted-foreground">Loading report schedules...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Automated Report Generation</h2>
        <p className="text-sm text-muted-foreground">Energy consumption reports, generated and emailed automatically on a schedule you set.</p>
      </div>

      <div className="border rounded-lg p-4 space-y-3">
        <h3 className="font-medium">New Schedule</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Frequency</Label>
            <Select value={form.frequency} onValueChange={(v: any) => setForm({ ...form, frequency: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Send Hour (0-23)</Label>
            <Input type="number" min={0} max={23} value={form.sendHour} onChange={(e) => setForm({ ...form, sendHour: Number(e.target.value) })} />
          </div>
          {form.frequency === 'weekly' && (
            <div>
              <Label>Day of Week</Label>
              <Select value={String(form.dayOfWeek)} onValueChange={(v) => setForm({ ...form, dayOfWeek: Number(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WEEKDAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {form.frequency === 'monthly' && (
            <div>
              <Label>Day of Month (1-28)</Label>
              <Input type="number" min={1} max={28} value={form.dayOfMonth} onChange={(e) => setForm({ ...form, dayOfMonth: Number(e.target.value) })} />
            </div>
          )}
          <div className="sm:col-span-2">
            <Label>Recipient Emails (comma-separated)</Label>
            <Input value={form.recipients} onChange={(e) => setForm({ ...form, recipients: e.target.value })} placeholder="manager@company.com, ops@company.com" />
          </div>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button onClick={createSchedule} disabled={saving}>
          <Plus className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Add Schedule'}
        </Button>
      </div>

      <div className="space-y-2">
        {schedules.length === 0 && (
          <p className="text-sm text-muted-foreground">No automated reports scheduled yet.</p>
        )}
        {schedules.map((s) => (
          <div key={s.id} className="flex items-center justify-between border rounded-md p-3">
            <div className="flex items-center gap-3 min-w-0">
              <Mail className={s.enabled ? 'h-4 w-4 text-blue-500 shrink-0' : 'h-4 w-4 text-muted-foreground shrink-0'} />
              <div className="min-w-0">
                <p className="font-medium">Energy Report — {describeSchedule(s)}</p>
                <p className="text-xs text-muted-foreground truncate">{s.recipients}</p>
                {s.lastSentAt && <p className="text-xs text-muted-foreground">Last sent: {new Date(s.lastSentAt).toLocaleString()}</p>}
                {s.lastReport && (
                  <div className="flex flex-wrap items-center gap-1 mt-1">
                    <Badge variant="outline" className={STATUS_TONE[s.lastReport.emailStatus]}>
                      {STATUS_LABEL[s.lastReport.emailStatus] || s.lastReport.emailStatus}
                    </Badge>
                    <Badge
                      variant="outline"
                      className={STATUS_TONE[s.lastReport.bridgeStatus]}
                      title={s.lastReport.bridgeError || undefined}
                    >
                      {STATUS_LABEL[s.lastReport.bridgeStatus] || s.lastReport.bridgeStatus}
                    </Badge>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button variant="ghost" size="icon" onClick={() => toggleEnabled(s)} aria-label={s.enabled ? 'Disable' : 'Enable'}>
                <Power className={s.enabled ? 'h-4 w-4 text-green-600' : 'h-4 w-4 text-muted-foreground'} />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => removeSchedule(s.id)} aria-label="Delete">
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
